import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { DATE_RANGE_PRESETS_BY_ID } from "@helpers/date";
import {
    useOrganizationUserCache,
    ORGANIZATION_USER_CACHE_KEYS,
} from "./useOrganizationUserCache";

const pinnedDatePresetsSchema = z.array(z.string());

interface UsePinnedDatePresetsOptions {
    cacheKey: ORGANIZATION_USER_CACHE_KEYS;
    defaults: readonly string[];
}

interface UsePinnedDatePresetsResult {
    pinnedPresets: readonly string[];
    onTogglePin: (id: string) => void;
}

export function usePinnedDatePresets({
    cacheKey,
    defaults,
}: UsePinnedDatePresetsOptions): UsePinnedDatePresetsResult {
    const [cached, setCached] = useOrganizationUserCache<string[]>(
        cacheKey,
        pinnedDatePresetsSchema,
    );
    // useOrganizationUserCache's setter only writes to localStorage; mirror in
    // local state so toggles re-render this component.
    const [source, setSource] = useState<readonly string[]>(
        () => cached ?? defaults,
    );

    const pinnedPresets = useMemo<readonly string[]>(
        () => source.filter((id) => Boolean(DATE_RANGE_PRESETS_BY_ID[id])),
        [source],
    );

    const onTogglePin = useCallback(
        (id: string) => {
            const next = source.includes(id)
                ? source.filter((presetId) => presetId !== id)
                : [...source, id];
            setSource(next);
            setCached(next);
        },
        [source, setCached],
    );

    return { pinnedPresets, onTogglePin };
}
